import '../dashboard/Dashboard.css';
import { useState,useEffect } from 'react';
import {useDashboardData} from './useDashboard';
import userProfileStore from "../core/UserProfileStore";
import useUserSettingStore from './UserSettingStore';
import {toTitleCase} from './model';
import { APP_ENV } from '../../constants';
import {accountingPOUrl,accountingPOAppLinkUrl} from './DashboardConfig';
import {usePODashboardFilter} from './filters/POFilter';
import moment from 'moment';

export const PO_DASHBOARD = {
  DASHBOARD_ID: 'po-dashboard',
  DOMAIN_ID: 'accounting',
  WIDGET_PO_DETAIL_ID: 'po-detail',
};


export const getGroups = (loginId:string) =>{
  const config = [];
  config.push( 
        {
          id: 'group',
          apiConfig: {
            method:'GET',
            headers:{
              'x-department-name':'',
              'x-department-type':'',
              'remote_user':loginId,
            },
            path:"/purchase-order-bff/user-details-with-account-type/A",
            endpoint: accountingPOUrl[APP_ENV as unknown as keyof typeof accountingPOUrl],
          },
        });
  return config;
}

export const getWidgets = (t:any) =>{
  return [
      {
          id: PO_DASHBOARD.WIDGET_PO_DETAIL_ID,
          dashboardId:PO_DASHBOARD.DASHBOARD_ID,
          isFilterAvailable: true,
          title: t('dashboard.accounting.po'),
          xKey: 'Type',
          isPricing:true,
          valueKey: 'amount',
          link:accountingPOAppLinkUrl[APP_ENV as unknown as keyof typeof accountingPOAppLinkUrl],
          availableViews: [ 'stacked-bar','bar', 'table' , 'grid' ],
          colors:{
            'Approved': '#2C4F81',
            'Pending': '#3365AD',
            'Exception': '#6998DE',
            'Closed': '#A8BDDD',
            'Voided': '#C3CFE1',
          },
          
          dataKey: ['Approved', 'Pending', 'Exception', 'Closed', 'Voided'],
          thresholdFields:[
            {name:"Approved"},
            {name:"Closed"},
            {name:"Exception"},
            {name:"Pending"},
            {name:"Voided"},
          ],
          defaultView: 'grid',
          dataMapping:{
            transform: (sourceData: any) => {
                const snapshotList = sourceData?.snapshotList || sourceData[0]?.snapshotList || [];
                const result =  snapshotList
                  .map((item: any) => {
                    if(item.poType === 'MIS'){
                      item.poType = 'Miscellaneous';
                    }
                    const row: any = { Type: toTitleCase(item.poType), totalCount: item.totalCount, name:toTitleCase(item.poType) };
                    let total = 0;
                    Object.entries(item.snapshotDetails || {}).forEach(([status, detail]: [string, any]) => {
                      let s = toTitleCase(status)
                      if(s === 'Approved For Pmt'){
                        s = 'Approved'
                      }
                      row[`${s}`] = detail?.amount || 0;
                      row[`${s}_count`] = detail?.count || 0;
                      total += detail?.amount || 0;
                    });
                    row.total = total;
                    return row;
                  })
                  .sort((a: any, b: any) => b.total - a.total); // Sort by total descending
                  return result;
              },
          }
      },
    ];
  }

export const getDashboardConfig = (group:string, 
  department:string,
  loginId:string,
  enterpriseId:string,
  storeId:string,
  dateFrom: string,
  dateTo: string) =>{
  const config = [];

  config.push( 
          {
            id: 'po-query',
            name: 'Overview',
            layout: 'default',
            apiConfig: {
              method:'POST',
              headers:{
                'x-department-name':department,
                'x-department-type':'A',
                'x-enterprise-id':enterpriseId,
                'x-store-id':storeId,
                'remote_user':loginId,
              },
              body:{
                "pageNumber":1,
                "rowsPerPage":50,
                "columnSort":{"columnName":"UPDATED_DATE","order":"DESC"},
                "poTypes":[],
                "poSubStatuses":[],
                "selectedStores":[],
                "allStores":[],
                "dateFrom":dateFrom,
                "dateTo":dateTo,
                "queryString":"",
                "poGroupCode":group,
                "minAmount":null,
                "maxAmount":null,
                "poAmount":null,
                "amountFilterOperator":null,
                "includePreInvoiced":false,
                "includeExceptions":false
              },
              path:"/purchase-order-bff/dashboard-po-data",
              endpoint: accountingPOUrl[APP_ENV as unknown as keyof typeof accountingPOUrl]
            },
          });
  return config;
}


export const usePODashboard = ({ enabled = true, filteredWidgets = [] }: any = {}) => {

  const dashboardId = PO_DASHBOARD.DASHBOARD_ID;
  const { userSetting } = useUserSettingStore();
  const { unifyUserProfile, enterpriseId } = userProfileStore();

  const {filterSetting, isLoading : loadingFilter , error : filterError } = usePODashboardFilter({userSetting, enabled});

  const [config, setConfig] = useState<any>(null);
  const [widgets, setWidgets] = useState<any[]>(filteredWidgets);


  const { isLoading, error, getWidgetData } = useDashboardData({
    config,
    enabled: enabled && !!config,
  });

  // Build main config
  useEffect(() => {
    if (!enabled) {
      setConfig(null);
      return;
    }
    if (!filterSetting ||!enterpriseId)
      return;

    const { group, storeId, department } = filterSetting;
    
    // Calculate date range: use startDate/endDate if available, otherwise calculate from dateRangePreset
    let dateFrom, dateTo;
    
    if (filterSetting.startDate && filterSetting.endDate) {
      dateFrom = filterSetting.startDate;
      dateTo = filterSetting.endDate;
    } else if (filterSetting.dateRangePreset) {
      // Convert dateRangePreset to number since it may come as a string
      const daysAgo = Number(filterSetting.dateRangePreset);
      dateTo = moment().format('YYYY-MM-DD');
      dateFrom = moment().subtract(daysAgo, 'days').format('YYYY-MM-DD');
    } else {
      // Default to Last Month (30 days)
      dateTo = moment().format('YYYY-MM-DD');
      dateFrom = moment().subtract(30, 'days').format('YYYY-MM-DD');
    }

    setConfig(
      getDashboardConfig(
        group, 
        department,
        unifyUserProfile.loginId,
        enterpriseId,
        storeId,
        dateFrom,
        dateTo
      )
    );
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSetting, enterpriseId, filterSetting?.startDate, filterSetting?.endDate, filterSetting?.dateRangePreset]);
  
  useEffect(() => {
    if (userSetting) {
      const dashboard = userSetting.dashboard?.find((dash: any) => dash.id === dashboardId);
      const savedWidgets = dashboard?.widgets || [];
      const updatedWidgets = filteredWidgets
        .map((dwidget: any) => {
          const savedWidget = savedWidgets.find((w: any) => w.id === dwidget.id);
          return {
            ...dwidget,
            fields: savedWidget?.fields,
            view: savedWidget?.view,
          };
        });
      setWidgets(updatedWidgets);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardId, filteredWidgets, userSetting]);
  
  // Get the complete filter from userSetting to preserve all properties
  const dashboardFilter = userSetting?.dashboard?.find((d: any) => d.id === dashboardId)?.filter || {};

    return {
    dashboardId,
    widgets,
    filter: dashboardFilter, // Return complete filter from userSetting
      isLoading: enabled ? (isLoading || loadingFilter) : false,
    error:filterError || error,
    getWidgetData,
  };

}

